import { useEffect, useRef, useState } from 'react'
import { useForm, Controller, useFormState } from 'react-hook-form'
import { Form } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { connect, useDispatch } from 'react-redux'
import { isArray, isEmpty, isNil, isObject, isString, omit, omitBy, pick, reduce } from 'lodash'

import routes from '../../routers'
import { createRecipe, getFamilyMembers, getRecipeById } from '../../api/recipe'
import { setLoading } from '../../redux/actions/ui'
import {
  ACTIVE_STATUS,
  CATEGORIES_RECIPE,
  DEFAULT_VALUES_ADD_RECIPE_FIRST_STEP,
  LIMIT_RECIPES,
  isFreePlan,
} from '../../utils/constants'
import useDirty from '../../utils/hooks/useDirty'
import { checkAbilityToCreate } from '../../utils/auth'
import { setIncreaseNumberRecipes } from '../../redux/actions/user'
import {
  MESSAGES,
  RECIPE_LIMITS,
  maxLengthCheck,
  minLengthCheck,
  recipeNameCharactersCheck,
  requiredCheck,
  scrollToFirstProblem,
  showServerErrors,
} from '../../utils/recipeLimits'
import FieldFeedback from '../../components/FieldFeedback'
import InputSimple from '../../components/InputSimple'
import CustomInputFile from '../../components/CustomInputFile'
import AudioMemory from '../../components/AudioMemory'
import ScanRecipeCard, { TERMS_URL } from '../../components/ScanRecipeCard'
import RecipeFromLink from '../../components/RecipeFromLink'
import RecipeCardPhotos, { MAX_CARD_PHOTOS } from '../../components/RecipeCardPhotos'
import { MorselFork } from '../../components/SvgIcon'
import LayoutRecipe from './components/LayoutRecipe'
import FamilyGroupPicker from './components/FamilyGroupPicker'
import WhoseRecipePicker, {
  WHOSE_MEMBER,
  WHOSE_MINE,
  WHOSE_SOMEONE,
  CREDITED_NAME_MAX,
  firstName,
} from './components/WhoseRecipePicker'

import './style.css'

const SOURCE_FIELDS = ['source_name', 'source_url']

// "www.example.com/pie" -> "https://www.example.com/pie"
const withWebAddress = (link = '') => {
  const trimmed = link.trim()
  return !trimmed || /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
}

// The API sends this stand-in picture for recipes without a food photo
const NO_PHOTO_PATTERN = /imageRecipeWithoutPhoto\.png$/
const realPhoto = (image) => (isString(image) && NO_PHOTO_PATTERN.test(image) ? '' : image)

// "Whose recipe is this?" as saved on a recipe, from the point of view of the person editing
const whoseFromRecipe = (recipe, userId) => {
  if (recipe?.credited_name) {
    return { recipe_for: WHOSE_SOMEONE, credited_name: recipe.credited_name }
  }
  if (recipe?.user_id && recipe.user_id !== userId) {
    return { recipe_for: WHOSE_MEMBER, for_user_id: recipe.user_id }
  }
  return { recipe_for: WHOSE_MINE }
}

const AddRecipe = ({ myGroups, user }) => {
  const dispatch = useDispatch()
  const navigation = useNavigate()
  const { filterDirtyFields } = useDirty()
  const [required, setRequired] = useState(true)
  const [to_publish, setToPublish] = useState(false)
  const [disableButton, setDisableButton] = useState(false)
  const { control, handleSubmit, reset, setValue, getValues, setError, clearErrors } = useForm()
  const { dirtyFields, errors } = useFormState({ control })
  // Problems the API found that aren't next to a field on this page
  const [problems, setProblems] = useState([])
  const [draftSaved, setDraftSaved] = useState(false)
  const [idRecipe, setIdRecipe] = useSearchParams({})
  const idRecipeForm = idRecipe.get('id')
  // ?start=scan or ?start=link: came from "Scan a recipe card" or "Paste a link"
  const startWith = idRecipe.get('start')
  const savedAudio = useRef(null)
  // Ingredients, steps and yield read off a scanned recipe card. They belong to
  // steps 2 and 3, so they are saved with this step and those steps load them.
  const scannedExtras = useRef(null)
  // How a new recipe was started (a card scan or a website), sent with its first save
  const startedFrom = useRef(null)
  // Photos of the recipe card: already saved, waiting to upload, and marked for removal
  const [savedCardPhotos, setSavedCardPhotos] = useState([])
  const [newCardPhotos, setNewCardPhotos] = useState([])
  const [removedCardPhotoIds, setRemovedCardPhotoIds] = useState([])
  const [fromWebsite, setFromWebsite] = useState(false)
  // Which "skip the typing" tool is open ('scan' or 'link'); the other tile is hidden meanwhile
  const [openStart, setOpenStart] = useState(null)
  // "Whose recipe is this?" and the family members it could be for
  const [whose, setWhose] = useState({ recipe_for: WHOSE_MINE })
  const [whoseChanged, setWhoseChanged] = useState(false)
  const [members, setMembers] = useState([])
  // A free plan that's full can still add recipes for family members (they count for them)
  const [onlyForFamily, setOnlyForFamily] = useState(false)
  // The form's names for "Whose recipe is this?" problems (the API uses the same ones)
  const WHOSE_FIELDS = ['recipe_for', 'credited_name']
  const startOpened = (key) => (isOpen) =>
    setOpenStart((current) => (isOpen ? key : current === key ? null : current))

  const onRecipeCardScanned = (scanned, photos = []) => {
    setNewCardPhotos(photos.slice(0, MAX_CARD_PHOTOS - savedCardPhotos.length))

    const setScanned = (key, value) => setValue(key, value, { shouldDirty: true })
    // Checked straight away, so anything too long shows in red before saving
    const setChecked = (key, value) =>
      setValue(key, value, { shouldDirty: true, shouldValidate: true })
    scanned?.name && setChecked('name', scanned.name)
    scanned?.description && setChecked('description', scanned.description)
    !isEmpty(scanned?.categories) && setScanned('categories', scanned.categories)

    scannedExtras.current = omitBy(
      pick(scanned, ['ingredients', 'steps', 'amount', 'measurement']),
      isEmpty
    )
    startedFrom.current = omitBy(
      {
        origin: 'scan',
        scan_batch_id: scanned?.scan_batch_id,
        scan_card_count: scanned?.scan_card_count || photos.length || undefined,
      },
      isNil
    )
  }

  // A recipe from a website: the name, ingredients, steps and servings, plus a credit.
  // The description and photo are left for the cook, so nothing of the site's own is copied.
  const onRecipeFromLink = (imported) => {
    const setImported = (key, value) => setValue(key, value, { shouldDirty: true })
    imported?.name && setValue('name', imported.name, { shouldDirty: true, shouldValidate: true })
    !isEmpty(imported?.categories) && setImported('categories', imported.categories)
    SOURCE_FIELDS.forEach((key) => setImported(key, imported?.[key] || ''))
    setFromWebsite(true)
    startedFrom.current = { origin: 'url_import' }

    scannedExtras.current = omitBy(
      pick(imported, ['ingredients', 'steps', 'amount', 'measurement']),
      isEmpty
    )
  }

  const categoriesSelected = (e, value, item, onChange) => {
    const filtered = !e.target.checked
      ? value?.filter((category) => item?.id !== category)
      : [...value, item?.id]
    onChange(filtered)
  }

  const chosenMember = members.find(({ id }) => id === whose.for_user_id)

  const changeWhose = (next) => {
    setWhose(next)
    setWhoseChanged(true)
    clearErrors(WHOSE_FIELDS)
    // Make sure the family member will be able to see it: share it with a group they're in
    const member =
      next.recipe_for === WHOSE_MEMBER && members.find(({ id }) => id === next.for_user_id)
    const groups = getValues('groups') || []
    if (member && !member.group_ids.some((id) => groups.includes(id))) {
      setValue('groups', [...groups, member.group_ids[0]], { shouldDirty: true })
    }
  }

  // [field, message] when "Whose recipe is this?" isn't finished
  const whoseProblem = () => {
    if (whose.recipe_for === WHOSE_MEMBER && !whose.for_user_id) {
      return ['recipe_for', 'Pick the family member this recipe is for.']
    }
    if (whose.recipe_for === WHOSE_SOMEONE && !(whose.credited_name || '').trim()) {
      return ['credited_name', 'Add the name of the person whose recipe it is.']
    }
    return null
  }

  // Shown next to the question, like the form's other field messages; false stops the save
  const checkWhose = () => {
    const problem = whoseChanged && whoseProblem()
    if (problem) {
      setError(problem[0], { type: 'manual', message: problem[1] })
      scrollToFirstProblem()
      return false
    }
    return true
  }

  const onSubmit = (data) => {
    if (!checkWhose()) return

    const newData = omit(
      filterDirtyFields({
        dirtyFields,
        data,
        current_step: 1,
        urlParams: idRecipeForm,
      }),
      'audio'
    )
    // Emptied credit fields are sent as empty so the old credit is removed
    SOURCE_FIELDS.forEach((key) => {
      if (dirtyFields[key]) {
        newData[key] = key === 'source_url' ? withWebAddress(data[key]) : (data[key] || '').trim()
      }
    })
    const checkAudio = isObject(data?.audio)
      ? { audio: data?.audio }
      : savedAudio.current && isEmpty(data?.audio)
      ? { remove_audio: true }
      : {}
    const checkFileImage = isObject(data?.image) ? { image: data?.image } : {}
    const checkEmptyFile =
      !isString(data?.image) || !isEmpty(data?.image) ? checkFileImage : { image: '' }

    const dataField = new FormData()
    for (const [key, value] of Object.entries({
      ...scannedExtras.current,
      ...(!idRecipeForm ? startedFrom.current : {}),
      ...newData,
      ...checkEmptyFile,
      ...checkAudio,
      ...(whoseChanged
        ? {
            recipe_for: whose.recipe_for,
            ...(whose.recipe_for === WHOSE_MEMBER && { for_user_id: whose.for_user_id }),
            ...(whose.recipe_for === WHOSE_SOMEONE && {
              credited_name: (whose.credited_name || '').trim(),
            }),
          }
        : {}),
      to_publish: to_publish && required,
    })) {
      dataField.append(key, isArray(value) ? JSON.stringify(value) : value)
    }
    newCardPhotos.forEach((photo, index) =>
      dataField.append('card_images[]', photo, `recipe-card-${index + 1}.jpg`)
    )
    if (!isEmpty(removedCardPhotoIds)) {
      dataField.append('remove_card_images', JSON.stringify(removedCardPhotoIds))
    }

    setDisableButton(true)
    setProblems([])
    createRecipe(dataField)
      .then(({ data }) => {
        savedAudio.current = data?.audio
        scannedExtras.current = null
        startedFrom.current = null
        setSavedCardPhotos(data?.cardImages || [])
        setNewCardPhotos([])
        setRemovedCardPhotoIds([])
        setValue('audio', data?.audio || '')
        setToPublish(required)
        setWhoseChanged(false)
        // Only the user's own recipes count toward their free plan
        !idRecipeForm && data?.user_id === user?.id && dispatch(setIncreaseNumberRecipes())
        if (required) {
          return navigation(`${routes.addRecipeSecond}?id=${data?.id}`)
        }
        setIdRecipe({ id: data?.id })
        setDraftSaved(true)
      })
      .catch((e) => {
        setProblems(showServerErrors(e, setError, 1))
        scrollToFirstProblem()
      })
      .finally(() => setDisableButton(false))
  }

  useEffect(() => {
    const checkUserLimit = isFreePlan(user) && user?.recipes_count >= LIMIT_RECIPES
    const familyMembers = getFamilyMembers()
      .then(({ data }) => (isArray(data) ? data : []))
      .catch(() => [])
    familyMembers.then(setMembers)
    if (isEmpty(idRecipeForm) && checkUserLimit) {
      // A full free plan can still add recipes for family members; otherwise, the upgrade note
      familyMembers.then((list) => {
        if (list.length) {
          setOnlyForFamily(true)
          setWhose({ recipe_for: WHOSE_MEMBER })
          setWhoseChanged(true)
        } else {
          checkAbilityToCreate(true)(dispatch, navigation)
        }
      })
    }
    if (!isEmpty(idRecipeForm)) {
      dispatch(setLoading(true))
      return getRecipeById(idRecipeForm)
        .then(({ data }) => {
          setWhose(whoseFromRecipe(data, user?.id))
          // Someone who left the family groups is still shown, so the choice isn't lost
          if (data?.user_id !== user?.id && data?.author) {
            familyMembers.then((list) =>
              setMembers(
                list.some(({ id }) => id === data.user_id)
                  ? list
                  : [...list, { ...pick(data.author, ['id', 'fullname', 'avatar']), group_ids: [] }]
              )
            )
          }
          savedAudio.current = data?.audio
          setSavedCardPhotos(data?.cardImages || [])
          const filterDataRecipe = filterDirtyFields({
            dirtyFields: DEFAULT_VALUES_ADD_RECIPE_FIRST_STEP,
            data,
          })
          setToPublish(data?.status === ACTIVE_STATUS)
          reset({ ...DEFAULT_VALUES_ADD_RECIPE_FIRST_STEP, ...filterDataRecipe })
          return reduce(
            filterDataRecipe,
            (res, value, key) => {
              setValue(key, value, { shouldDirty: true })
            },
            {}
          )
        })
        .catch(() => navigation(routes.notFound))
        .finally(() => dispatch(setLoading(false)))
    }
    reset(DEFAULT_VALUES_ADD_RECIPE_FIRST_STEP)
  }, [])

  return (
    <LayoutRecipe
      step={1}
      navigationNext={routes.addRecipeSecond}
      navigationPrevTitle="Next: ingredients"
      title={idRecipeForm ? 'Edit your recipe' : 'Add a new recipe'}
      subTitle={
        idRecipeForm ? '' : 'It only takes a few minutes, and it will last for generations.'
      }
      onSubmit={(e) => {
        // Earlier "Whose recipe is this?" messages aren't tied to an input, so clear them
        // here or they would stop every later save
        clearErrors(WHOSE_FIELDS)
        return handleSubmit(onSubmit, scrollToFirstProblem)(e)
      }}
      disableButtonSave={disableButton}
      isLoading={disableButton}
      setRequired={setRequired}
      problems={problems}
      draftSaved={draftSaved}
      onDraftSavedClose={() => setDraftSaved(false)}
    >
      {!idRecipeForm && (
        <section className="formSection formSectionStart">
          <h2 className="formSectionTitle">
            Skip the typing <span className="formLabelHint">Optional</span>
          </h2>
          <div className="formStartOptions">
            <ScanRecipeCard
              onScanned={onRecipeCardScanned}
              onActiveChange={startOpened('scan')}
              hidden={openStart === 'link'}
              suggested={startWith === 'scan'}
            />
            <RecipeFromLink
              onImported={onRecipeFromLink}
              onActiveChange={startOpened('link')}
              hidden={openStart === 'scan'}
              startOpen={startWith === 'link'}
            />
          </div>
          <p className="formStartFinePrint">
            Only save recipes you have the right to use. We never copy a website's photos or words.{' '}
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
          </p>
        </section>
      )}
      <section className="formSection">
        <h2 className="formSectionTitle">The basics</h2>
        <p className="formLabel">Recipe name</p>
        <Controller
          name="name"
          control={control}
          rules={{
            validate: {
              required: requiredCheck(MESSAGES.nameRequired, required),
              notEmptyForm: (value) =>
                !isEmpty(dirtyFields) || !isEmpty(value) || MESSAGES.nameRequired,
              minLength: minLengthCheck(MESSAGES.nameMin, RECIPE_LIMITS.name.min),
              maxLength: maxLengthCheck('Recipe names', RECIPE_LIMITS.name.max),
              characters: recipeNameCharactersCheck,
            },
          }}
          defaultValue=""
          render={({ field, fieldState }) => (
            <div data-field>
              <InputSimple
                {...field}
                {...fieldState}
                showErrorText={false}
                nameInputPlaceholder="For example, Grandma June's apple pie"
                styleInput="inputRecipeTitle grayPlaceholder"
                className="innerRecipeName"
              />
              <FieldFeedback
                error={fieldState.error}
                value={field.value}
                max={RECIPE_LIMITS.name.max}
                label="Recipe name"
              />
            </div>
          )}
        />
        <div className="formField">
          <p className="formLabel">
            Categories <span className="formLabelHint">Pick one or more</span>
          </p>
          <div className="recipeCategoryChips">
            {CATEGORIES_RECIPE.map((item, index) => (
              <Controller
                name="categories"
                key={index}
                control={control}
                rules={{
                  validate: {
                    required: (value) =>
                      !required || !isEmpty(value) || MESSAGES.categoriesRequired,
                    notEmptyForm: (value) =>
                      !isEmpty(dirtyFields) || !isEmpty(value) || MESSAGES.categoriesRequired,
                  },
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <label
                    className={`recipeCategoryChip ${value?.includes(item?.id) ? 'selected' : ''} ${
                      error ? 'hasError' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      value={item?.id}
                      checked={!!value?.includes(item?.id)}
                      onChange={(e) => categoriesSelected(e, value, item, onChange)}
                    />
                    {item?.name}
                  </label>
                )}
              />
            ))}
          </div>
          <FieldFeedback error={errors.categories} />
        </div>
        <p className="formLabel">
          Summary <span className="formLabelHint">Optional</span>
        </p>
        <Controller
          name="description"
          control={control}
          rules={{
            validate: {
              minLength: minLengthCheck(MESSAGES.descriptionMin, RECIPE_LIMITS.description.min),
              maxLength: maxLengthCheck('Summaries', RECIPE_LIMITS.description.max),
            },
          }}
          render={({ field, fieldState: { error } }) => {
            return (
              <div data-field>
                <Form.Control
                  {...field}
                  as="textarea"
                  className={`textAreaDescription grayPlaceholder ${error ? 'borderError' : ''}`}
                  placeholder={
                    fromWebsite
                      ? "In your own words: what's this dish like, and why do you love it? Please don't copy the website's description."
                      : 'Write a short summary or description of your delicious recipe here.'
                  }
                />
                <FieldFeedback
                  error={error}
                  value={field.value}
                  max={RECIPE_LIMITS.description.max}
                  label="Summary"
                />
              </div>
            )
          }}
        />
      </section>
      <section className="formSection">
        <h2 className="formSectionTitle">Photos and memories</h2>
        <Controller
          name="image"
          control={control}
          defaultValue=""
          rules={{
            required: false,
          }}
          render={({ field, fieldState }) => (
            <>
              <p className="formLabel">Food photo</p>
              {fromWebsite && (
                <p className="recipeSourceHint">
                  Made it? Add a photo of your own finished dish. We don't copy photos from
                  websites, and yours will mean more to your family.
                </p>
              )}
              <CustomInputFile
                {...field}
                {...fieldState}
                value={realPhoto(field.value)}
                textButton="Upload a food photo"
                emptyContent={
                  <span className="foodPhotoEmpty">
                    <MorselFork color="#e6ddd5" className="foodPhotoEmptyMark" />
                    <span className="foodPhotoEmptyText">Show off the finished dish</span>
                  </span>
                }
              />
            </>
          )}
        />
        <RecipeCardPhotos
          saved={savedCardPhotos}
          pending={newCardPhotos}
          onAdd={(photos) => {
            clearErrors('card_images')
            setNewCardPhotos([...newCardPhotos, ...photos])
          }}
          onRemovePending={(index) => {
            clearErrors('card_images')
            setNewCardPhotos(newCardPhotos.filter((_, i) => i !== index))
          }}
          onRemoveSaved={(id) => {
            clearErrors('card_images')
            setSavedCardPhotos(savedCardPhotos.filter((photo) => photo.id !== id))
            setRemovedCardPhotoIds([...removedCardPhotoIds, id])
          }}
        />
        <FieldFeedback error={errors.card_images} />
        <Controller
          name="audio"
          control={control}
          defaultValue=""
          rules={{
            required: false,
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <>
              <AudioMemory value={value} onChange={onChange} />
              <FieldFeedback error={error} />
            </>
          )}
        />
      </section>
      <section className="formSection" data-field="recipe_for">
        <h2 className="formSectionTitle">
          Whose recipe is this? <span className="formLabelHint">Optional</span>
        </h2>
        <p className="formSectionHint">
          {onlyForFamily
            ? 'Your free plan is full, but you can still add recipes for your family.'
            : 'Helping someone add theirs? Pick them here.'}
        </p>
        <WhoseRecipePicker
          value={whose}
          onChange={changeWhose}
          members={members}
          onlyMembers={onlyForFamily}
        />
        <FieldFeedback
          error={errors.recipe_for || errors.credited_name}
          value={whose.recipe_for === WHOSE_SOMEONE ? whose.credited_name : ''}
          max={whose.recipe_for === WHOSE_SOMEONE ? CREDITED_NAME_MAX : 0}
          label="Their name"
        />
      </section>
      <section className="formSection">
        <h2 className="formSectionTitle">Share it with your family</h2>
        <p className="formSectionHint">
          {whose.recipe_for === WHOSE_MEMBER && chosenMember
            ? `Pick a group ${firstName(chosenMember.fullname)} is in, so they can see it.`
            : 'Pick who gets to see it. You can change this later.'}
        </p>
        <Controller
          name="groups"
          control={control}
          rules={{
            required: false,
          }}
          render={({ field: { onChange, value } }) => (
            <FamilyGroupPicker groups={myGroups} value={value} onChange={onChange} />
          )}
        />
        <FieldFeedback error={errors.groups} />
      </section>
      <section className="formSection">
        <h2 className="formSectionTitle">
          Where's it from? <span className="formLabelHint">Optional</span>
        </h2>
        <p className="formSectionHint">Give credit to the cook, cookbook or website.</p>
        <div className="recipeSourceFields">
          <div className="formField">
            <label className="formLabel" htmlFor="recipeSourceName">
              Who it's from
            </label>
            <Controller
              name="source_name"
              control={control}
              defaultValue=""
              rules={{
                required: false,
                validate: {
                  maxLength: maxLengthCheck("Who it's from", RECIPE_LIMITS.sourceName.max),
                },
              }}
              render={({ field, fieldState }) => (
                <InputSimple
                  {...field}
                  {...fieldState}
                  controlIdInput="recipeSourceName"
                  nameInputPlaceholder="A cookbook, chef or website"
                  styleInput="inputRecipeSource"
                  className="innerRecipeSource"
                />
              )}
            />
          </div>
          <div className="formField">
            <label className="formLabel" htmlFor="recipeSourceUrl">
              Link to the original
            </label>
            <Controller
              name="source_url"
              control={control}
              defaultValue=""
              rules={{
                required: false,
                validate: {
                  maxLength: maxLengthCheck('The link', RECIPE_LIMITS.sourceUrl.max),
                  webAddress: (value) =>
                    !value ||
                    /^(https?:\/\/)?[^\s/]+\.[^\s]+$/i.test(value.trim()) ||
                    'Please paste a web address, for example https://www.example.com/recipe',
                },
              }}
              render={({ field, fieldState }) => (
                <InputSimple
                  {...field}
                  {...fieldState}
                  controlIdInput="recipeSourceUrl"
                  inputType="url"
                  nameInputPlaceholder="https://"
                  styleInput="inputRecipeSource"
                  className="innerRecipeSource"
                />
              )}
            />
          </div>
        </div>
      </section>
    </LayoutRecipe>
  )
}

export default connect(({ groups, user }) => ({
  myGroups: groups.data,
  user: user.data,
}))(AddRecipe)
